import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaGavel,
  FaArrowLeft,
  FaClock,
  FaTrophy,
  FaCheckCircle,
  FaTimes,
  FaExpand,
  FaUsers,
  FaChevronUp,
  FaChevronDown,
  FaFire,
  FaCreditCard,
  FaTruck,
  FaStore,
  FaCrown,
  FaMapMarkerAlt,
  FaEdit,
  FaTag,
} from "react-icons/fa";
import { useAuth } from "../../contexts/AuthContext";
import EmailOTPModal from "../../components/EmailOTPModal/EmailOTPModal";
import LoadingPage from "../../components/Loading/LoadingPage";
import {
  fetchAuctionItems,
  placeBid,
  getPaymentStatus,
  setupPayment,
  confirmSetup,
  setupPaypal,
  confirmPaypal,
  // setupVenmoVault,
  // confirmVenmo,
  submitWinnerFulfillment,
  retryWinnerPayment,
  buyNow,
} from "../../services/auctionService";
// import VenmoLinkButton from "../../components/Ticketing/VenmoLinkButton";
import "./SilentAuctionPage.css";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
const POLL_INTERVAL_OPEN = 3_000;
const POLL_INTERVAL_CLOSED = 30_000;
const SUGGEST_INCREMENT = 10;
const DELIVERY_FEE = 5;
const PENDING_BID_KEY = "auction_pending_bid_item";

function formatDollars(amount) {
  const n = parseFloat(amount);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
}

function isLoggedIn() {
  return !!(sessionStorage.getItem("perks_email") || localStorage.getItem("perks_email"));
}

// BtnSpinner removed — use <span className="btn-spinner" /> everywhere

// ── Keyboard scroll-reset hook ─────────────────────────────────────────────────
// On mobile, when the virtual keyboard is dismissed the OS restores the viewport
// height but the scroll position inside a fixed+overflow-auto bottom-sheet stays
// wherever the browser had scrolled to show the focused input.  This hook watches
// visualViewport for a height *increase* (= keyboard closed) and scrolls the
// modal element back to the top so the user can see the whole form again.
function useKeyboardScrollReset(modalRef) {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let lastHeight = vv.height;
    const handler = () => {
      const h = vv.height;
      if (h > lastHeight + 80 && modalRef.current) {
        modalRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
      lastHeight = h;
    };
    vv.addEventListener("resize", handler);
    return () => vv.removeEventListener("resize", handler);
  }, [modalRef]);
}

// ── Payment Banner ─────────────────────────────────────────────────────────────

const SAVE_PREF_KEY = "auction_payment_save_pref";

function PaymentBanner({
  paymentStatus, onLinkCard, onLinkPaypal,
  linkingCard, linkingPaypal, confirmingPayment, shakePayment,
  // venmoSetupToken, onVenmoApprove, onVenmoError,
}) {
  const [saveForNext, setSaveForNext] = useState(true);
  const [showSwitch, setShowSwitch] = useState(false);
  // const [showVenmo, setShowVenmo] = useState(false);

  const anyBusy = linkingCard || linkingPaypal;

  const handleLinkCard = () => {
    localStorage.setItem(SAVE_PREF_KEY, saveForNext ? "1" : "0");
    onLinkCard();
  };

  const handleLinkPaypal = () => {
    localStorage.setItem(SAVE_PREF_KEY, saveForNext ? "1" : "0");
    onLinkPaypal();
  };

  if (confirmingPayment) {
    return (
      <div className="auction-payment-banner auction-payment-banner--confirming">
        <span className="btn-spinner btn-spinner--dark" />
        <span>Confirming your payment method…</span>
      </div>
    );
  }

  if (!paymentStatus) return null;

  if (paymentStatus.has_payment_method) {
    const label = paymentStatus.type === "paypal"
      ? `PayPal — ${paymentStatus.paypal_email || "linked"}`
      // : paymentStatus.type === "venmo"
      // ? `Venmo — @${paymentStatus.venmo_handle || "linked"}`
      : `${paymentStatus.brand ? paymentStatus.brand.charAt(0).toUpperCase() + paymentStatus.brand.slice(1) : "Card"} ····${paymentStatus.last4}`;

    return (
      <div className={`auction-payment-banner ${showSwitch ? "auction-payment-banner--required" : "auction-payment-banner--linked"}`}>
        <FaCreditCard />
        <div className="auction-payment-banner-body">
          <span>
            {showSwitch
              ? <>Replace <strong>{label}</strong> with:</>
              : <>Payment on file: <strong>{label}</strong> — you're ready to bid. If you win, you'll be auto-charged.</>
            }
          </span>
        </div>
        {!showSwitch ? (
          <button
            className="auction-payment-banner-btn auction-payment-banner-btn--change"
            onClick={() => setShowSwitch(true)}
          >
            Change
          </button>
        ) : (
          <div className="auction-payment-banner-actions">
            <button
              className="auction-payment-banner-btn"
              onClick={handleLinkCard}
              disabled={anyBusy}
            >
              {linkingCard ? <span className="btn-spinner" /> : "Card"}
            </button>
            <button
              className="auction-payment-banner-btn auction-payment-banner-btn--paypal"
              onClick={handleLinkPaypal}
              disabled={anyBusy}
            >
              {linkingPaypal ? <span className="btn-spinner" /> : "PayPal"}
            </button>
            <button
              className="auction-payment-banner-btn auction-payment-banner-btn--cancel"
              onClick={() => setShowSwitch(false)}
              disabled={anyBusy}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="auction-payment-banner auction-payment-banner--required">
      <FaCreditCard />
      <div className="auction-payment-banner-body">
        <span>Add a payment method to bid. <strong>Only charged if you win.</strong></span>
        <label className="auction-payment-save-label">
          <input
            type="checkbox"
            checked={saveForNext}
            onChange={(e) => setSaveForNext(e.target.checked)}
          />
          Save for future auctions
        </label>
      </div>
      <div className="auction-payment-banner-actions">
        <button
          className={`auction-payment-banner-btn${shakePayment ? " auction-payment-banner-btn--shake" : ""}`}
          onClick={handleLinkCard}
          disabled={anyBusy}
        >
          {linkingCard ? <span className="btn-spinner" /> : "Link Card"}
        </button>
        <button
          className={`auction-payment-banner-btn auction-payment-banner-btn--paypal${shakePayment ? " auction-payment-banner-btn--shake" : ""}`}
          onClick={handleLinkPaypal}
          disabled={anyBusy}
        >
          {linkingPaypal ? <span className="btn-spinner" /> : "Link PayPal"}
        </button>
        {/* Venmo link disabled
        <button
          className="auction-payment-banner-btn auction-payment-banner-btn--venmo"
          onClick={() => setShowVenmo((v) => !v)}
          disabled={anyBusy}
        >
          Link Venmo
        </button>
        */}
      </div>
      {/* Venmo link panel disabled
      {showVenmo && (
        <div style={{ marginTop: 12, maxWidth: 280 }}>
          <VenmoLinkButton
            createVaultSetupToken={venmoSetupToken}
            onApprove={(vaultSetupToken) => {
              setShowVenmo(false);
              onVenmoApprove(vaultSetupToken);
            }}
            onError={onVenmoError}
          />
          <p className="auction-venmo-note">Authorize once — you'll be auto-charged if you win.</p>
        </div>
      )}
      */}
    </div>
  );
}

// ── Phone formatting ───────────────────────────────────────────────────────────

/** Format a raw digit string (up to 10 digits) as (XXX) XXX-XXXX. */
function formatPhone(digits) {
  const d = digits.slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function PhoneInput({ value, onChange, ...rest }) {
  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    onChange(formatPhone(digits));
  };
  return (
    <input
      type="tel"
      inputMode="numeric"
      maxLength={14} /* (XXX) XXX-XXXX = 14 chars */
      value={value}
      onChange={handleChange}
      {...rest}
    />
  );
}

// ── Address Autocomplete (OpenStreetMap Nominatim — free, no API key) ──────────

/** Parse a previously-saved merged address back into components for editing.
 *  Formats:
 *   "123 Main St, Chicago, IL 60601"
 *   "123 Main St, Apt 4B, Chicago, IL 60601"  (with line 2)
 */
function parseDeliveryAddress(addr) {
  if (!addr) return { street: "", street2: "", city: "", state: "", zip: "" };
  // With line 2: street, line2, city, ST zip
  const m2 = addr.match(/^(.+?),\s*(.+?),\s*(.+?),\s*([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
  if (m2) return { street: m2[1].trim(), street2: m2[2].trim(), city: m2[3].trim(), state: m2[4].trim(), zip: m2[5].trim() };
  // Without line 2: street, city, ST zip
  const m1 = addr.match(/^(.+?),\s*(.+?),\s*([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
  if (m1) return { street: m1[1].trim(), street2: "", city: m1[2].trim(), state: m1[3].trim(), zip: m1[4].trim() };
  return { street: addr, street2: "", city: "", state: "", zip: "" };
}

// US state name → 2-letter abbreviation (Photon returns full names)
const US_STATE_ABBR = {
  "Alabama":"AL","Alaska":"AK","Arizona":"AZ","Arkansas":"AR","California":"CA",
  "Colorado":"CO","Connecticut":"CT","Delaware":"DE","Florida":"FL","Georgia":"GA",
  "Hawaii":"HI","Idaho":"ID","Illinois":"IL","Indiana":"IN","Iowa":"IA","Kansas":"KS",
  "Kentucky":"KY","Louisiana":"LA","Maine":"ME","Maryland":"MD","Massachusetts":"MA",
  "Michigan":"MI","Minnesota":"MN","Mississippi":"MS","Missouri":"MO","Montana":"MT",
  "Nebraska":"NE","Nevada":"NV","New Hampshire":"NH","New Jersey":"NJ","New Mexico":"NM",
  "New York":"NY","North Carolina":"NC","North Dakota":"ND","Ohio":"OH","Oklahoma":"OK",
  "Oregon":"OR","Pennsylvania":"PA","Rhode Island":"RI","South Carolina":"SC",
  "South Dakota":"SD","Tennessee":"TN","Texas":"TX","Utah":"UT","Vermont":"VT",
  "Virginia":"VA","Washington":"WA","West Virginia":"WV","Wisconsin":"WI","Wyoming":"WY",
  "District of Columbia":"DC",
};

function AddressAutocomplete({ value, onChange, onSelect, onClearFields, hasError }) {
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const abortRef = useRef(null);

  const search = useCallback(async (q) => {
    if (q.length < 4) { setSuggestions([]); setOpen(false); setSearching(false); return; }
    // Cancel any in-flight request
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setSearching(true);
    setOpen(true);
    try {
      // Photon — purpose-built for fast OSM autocomplete, no API key needed
      const res = await fetch(
        `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=6&lang=en`,
        { signal: abortRef.current.signal }
      );
      const data = await res.json();
      const filtered = (data.features || []).filter((f) => {
        const p = f.properties || {};
        return p.countrycode === "US" && (p.street || p.housenumber);
      });
      setSuggestions(filtered);
      setOpen(true);
    } catch (err) {
      if (err.name !== "AbortError") { setSuggestions([]); setOpen(false); }
    } finally {
      setSearching(false);
    }
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    onClearFields();
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 250);
  };

  const handleSelect = (feature) => {
    const p = feature.properties || {};
    const street = [`${p.housenumber || ""}`, p.street || ""].filter(Boolean).join(" ").trim();
    const city   = p.city || p.town || p.village || p.municipality || "";
    const state  = US_STATE_ABBR[p.state] || p.state || "";
    const zip    = p.postcode || "";

    onChange(street);
    onSelect({ street, city, state, zip });
    setSuggestions([]);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") { setOpen(false); setSuggestions([]); e.stopPropagation(); }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (!containerRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={containerRef} className="auction-address-autocomplete">
      <input
        type="text"
        className={`auction-field-input${hasError ? " auction-field-input--error" : ""}`}
        placeholder="Start typing your street address…"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        spellCheck={false}
      />
      {open && (
        <ul className="auction-address-suggestions" role="listbox">
          {searching ? (
            <li className="auction-address-suggestion-loading" role="option" aria-selected={false}>
              <span className="btn-spinner btn-spinner--dark" />
              <span>Searching…</span>
            </li>
          ) : suggestions.length === 0 ? (
            <li className="auction-address-suggestion-empty" role="option" aria-selected={false}>
              No matches — try a more specific address
            </li>
          ) : suggestions.map((f, i) => {
            const p = f.properties || {};
            const primary = [`${p.housenumber || ""}`, p.street || ""].filter(Boolean).join(" ").trim() || p.name || "";
            const secondary = [p.city || p.town || p.village, US_STATE_ABBR[p.state] || p.state, p.postcode].filter(Boolean).join(", ");
            return (
              <li
                key={p.osm_id ?? i}
                role="option"
                aria-selected={false}
                className="auction-address-suggestion-item"
                onMouseDown={(e) => { e.preventDefault(); handleSelect(f); }}
              >
                <span className="auction-address-suggestion-primary">{primary}</span>
                {secondary && <span className="auction-address-suggestion-secondary">{secondary}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ── Fulfillment Modal ──────────────────────────────────────────────────────────

function FulfillmentModal({ item, customerToken, paymentStatus, taxPercent, venue, onClose, onSuccess }) {
  // Default to "pickup_now" for first-time claims (they're at the event); keep
  // existing preference when editing. "pickup_now" means taking it home tonight.
  const [preference, setPreference] = useState(item.delivery_preference || "pickup_now");

  // Address components — pre-parse existing address when editing, default to St. Louis for new
  const existing = parseDeliveryAddress(item.delivery_address);
  const [street, setStreet] = useState(existing.street);
  const [street2, setStreet2] = useState(existing.street2 || "");
  const [city, setCity] = useState(existing.city || "St. Louis");
  const [stateVal, setStateVal] = useState(existing.state || "MO");
  const [zip, setZip] = useState(existing.zip);

  // Phone — required; pre-fill from winner_phone if available, then fall back
  // to the phone saved on the customer's loyalty profile (paymentStatus.customer_phone).
  const [phone, setPhone] = useState(() => {
    if (item.winner_phone) {
      return formatPhone(item.winner_phone.replace(/\D/g, ""));
    }
    const profilePhone = paymentStatus?.customer_phone;
    if (profilePhone) {
      // phone_e164 is "+1XXXXXXXXXX" — strip non-digits then take last 10
      const digits = profilePhone.replace(/\D/g, "").slice(-10);
      return formatPhone(digits);
    }
    return "";
  });

  const [preferredTime, setPreferredTime] = useState(item.preferred_time || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [errorField, setErrorField] = useState(null); // which field to highlight

  // Ref for the modal sheet element — used by keyboard-scroll-reset hook
  const modalRef = useRef(null);
  useKeyboardScrollReset(modalRef);

  // Field refs for scroll-to + focus on error
  const phoneFieldRef   = useRef(null);
  const timeFieldRef    = useRef(null);
  const streetFieldRef  = useRef(null);
  const addrRowRef      = useRef(null); // city/state/zip row

  const focusField = (field, ref) => {
    setErrorField(field);
    // Small delay lets state settle before scrollIntoView
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      ref.current?.querySelector("input")?.focus();
    }, 40);
  };

  const isEditing = Boolean(item.delivery_preference);
  const tax = taxPercent || 0;
  const winningBid = parseFloat(item.sold_price || item.current_bid);
  const bidTax = winningBid * (tax / 100);
  const bidTotal = winningBid + bidTax;
  const deliveryFeeBase = DELIVERY_FEE;
  const deliveryTax = deliveryFeeBase * (tax / 100);
  const deliveryTotal = deliveryFeeBase + deliveryTax;

  const paymentLabel = paymentStatus?.type === "paypal"
    ? `PayPal (${paymentStatus.paypal_email || "linked"})`
    : paymentStatus?.last4
      ? `${paymentStatus.brand || "Card"} ····${paymentStatus.last4}`
      : "your saved payment method";

  const venueDisplay = venue
    ? [venue.venue_name, venue.address_1, venue.city].filter(Boolean).join(", ")
    : "event venue";

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setErrorField(null);
    if (phone.replace(/\D/g, "").length < 10) {
      setError("Please enter a valid 10-digit phone number.");
      focusField("phone", phoneFieldRef);
      return;
    }
    if (preference === "delivery") {
      if (!street.trim())   { setError("Please enter a street address."); focusField("street", streetFieldRef); return; }
      if (!city.trim())     { setError("Please enter a city.");           focusField("city",   addrRowRef);     return; }
      if (!stateVal.trim()) { setError("Please enter a state.");          focusField("state",  addrRowRef);     return; }
      if (!zip.trim())      { setError("Please enter a ZIP code.");       focusField("zip",    addrRowRef);     return; }
    }
    // pickup_now needs no scheduled time — winner is taking it home tonight
    if (preference !== "pickup_now" && !preferredTime) {
      setError("Please select a preferred date and time.");
      focusField("time", timeFieldRef);
      return;
    }

    const mergedAddress = preference === "delivery"
      ? [street.trim(), street2.trim() || null, `${city.trim()}, ${stateVal.trim()} ${zip.trim()}`]
          .filter(Boolean).join(", ")
      : undefined;

    setSubmitting(true);
    try {
      await submitWinnerFulfillment(item.event_id, item.item_id, {
        customer_token: customerToken,
        delivery_preference: preference,
        delivery_address: mergedAddress,
        preferred_time: preference === "pickup_now" ? null : preferredTime.trim(),
        winner_phone: phone.trim(),
      });
      onSuccess(preference);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auction-modal-overlay" role="dialog" aria-modal="true">
      <div className="auction-modal" ref={modalRef}>
        <button className="auction-modal-close" onClick={onClose} aria-label="Close">
          <FaTimes />
        </button>

        <div className="auction-modal-header">
          <div className="auction-modal-gavel-wrap auction-fulfillment-crown">
            {isEditing ? <FaEdit /> : <FaCrown />}
          </div>
          <h2 className="auction-modal-heading">{isEditing ? "Update Delivery" : "You Won!"}</h2>
          <p className="auction-modal-item-name">{item.title}</p>
          {item.artist_name && (
            <p className="auction-modal-artist">by {item.artist_name}</p>
          )}
        </div>

        <div className="auction-modal-current-bid">
          <div className="auction-modal-current-inner">
            <span className="auction-modal-current-label">Winning Bid — auto-charged</span>
            <span className="auction-modal-current-value">
              {formatDollars(winningBid)}
            </span>
          </div>
        </div>

        <form className="auction-bid-form" onSubmit={handleSubmit}>
          <p className="auction-fulfillment-intro">
            {isEditing
              ? "Update how you'd like to receive your item. Switching to delivery will charge the delivery fee."
              : "How would you like to receive your item?"}
          </p>

          <div className="auction-fulfillment-options">
            <label className={`auction-fulfillment-option ${preference === "pickup_now" ? "auction-fulfillment-option--active" : ""}`}>
              <input
                type="radio"
                name="preference"
                value="pickup_now"
                checked={preference === "pickup_now"}
                onChange={() => setPreference("pickup_now")}
              />
              <FaCheckCircle className="auction-fulfillment-option-icon" />
              <div>
                <strong>Pick Up Now — Free</strong>
                <span>I'm taking it home tonight</span>
              </div>
            </label>

            <label className={`auction-fulfillment-option ${preference === "pickup" ? "auction-fulfillment-option--active" : ""}`}>
              <input
                type="radio"
                name="preference"
                value="pickup"
                checked={preference === "pickup"}
                onChange={() => setPreference("pickup")}
              />
              <FaStore className="auction-fulfillment-option-icon" />
              <div>
                <strong>Schedule Pick-Up — Free</strong>
                <span className="auction-fulfillment-venue">
                  <FaMapMarkerAlt /> {venueDisplay}
                </span>
              </div>
            </label>

            <label className={`auction-fulfillment-option ${preference === "delivery" ? "auction-fulfillment-option--active" : ""}`}>
              <input
                type="radio"
                name="preference"
                value="delivery"
                checked={preference === "delivery"}
                onChange={() => setPreference("delivery")}
              />
              <FaTruck className="auction-fulfillment-option-icon" />
              <div>
                <strong>Delivery — +${DELIVERY_FEE}</strong>
                <span>Shipped to your address</span>
              </div>
            </label>
          </div>

          {/* ── Phone number (always required) ─────────────────────────────── */}
          <div ref={phoneFieldRef} className={`auction-field${errorField === "phone" ? " auction-field--error" : ""}`}>
            <label className="auction-field-label">Phone Number *</label>
            <PhoneInput
              className={`auction-field-input${errorField === "phone" ? " auction-field-input--error" : ""}`}
              placeholder="(314) 555-0100"
              value={phone}
              onChange={(v) => { setPhone(v); setError(""); setErrorField(null); }}
            />
          </div>

          {/* ── Address fields (delivery only) ─────────────────────────────── */}
          {preference === "delivery" && (
            <>
              <div ref={streetFieldRef} className={`auction-field${errorField === "street" ? " auction-field--error" : ""}`}>
                <label className="auction-field-label">Street Address *</label>
                <AddressAutocomplete
                  value={street}
                  hasError={errorField === "street"}
                  onChange={(v) => { setStreet(v); setError(""); setErrorField(null); }}
                  onClearFields={() => { setZip(""); }}
                  onSelect={({ street: s, city: c, state: st, zip: z }) => {
                    setStreet(s);
                    if (c) setCity(c);
                    if (st) setStateVal(st);
                    if (z) setZip(z);
                    setError(""); setErrorField(null);
                  }}
                />
              </div>
              <div className="auction-field">
                <label className="auction-field-label">
                  Apt, Suite, Unit <span className="auction-field-optional">(optional)</span>
                </label>
                <input
                  type="text"
                  className="auction-field-input"
                  placeholder="Apt 4B, Suite 200…"
                  value={street2}
                  onChange={(e) => setStreet2(e.target.value)}
                />
              </div>
              <div ref={addrRowRef} className="auction-address-row">
                <div className={`auction-field${errorField === "city" ? " auction-field--error" : ""}`}>
                  <label className="auction-field-label">City *</label>
                  <input
                    type="text"
                    className={`auction-field-input${errorField === "city" ? " auction-field-input--error" : ""}`}
                    placeholder="St. Louis"
                    value={city}
                    onChange={(e) => { setCity(e.target.value); setError(""); setErrorField(null); }}
                  />
                </div>
                <div className={`auction-field auction-field--state${errorField === "state" ? " auction-field--error" : ""}`}>
                  <label className="auction-field-label">State *</label>
                  <input
                    type="text"
                    className={`auction-field-input${errorField === "state" ? " auction-field-input--error" : ""}`}
                    placeholder="MO"
                    maxLength={2}
                    value={stateVal}
                    onChange={(e) => { setStateVal(e.target.value.toUpperCase()); setError(""); setErrorField(null); }}
                  />
                </div>
                <div className={`auction-field auction-field--zip${errorField === "zip" ? " auction-field--error" : ""}`}>
                  <label className="auction-field-label">ZIP *</label>
                  <input
                    type="text"
                    className={`auction-field-input${errorField === "zip" ? " auction-field-input--error" : ""}`}
                    placeholder="60601"
                    maxLength={10}
                    value={zip}
                    onChange={(e) => { setZip(e.target.value); setError(""); setErrorField(null); }}
                  />
                </div>
              </div>
            </>
          )}

          {preference !== "pickup_now" && (
            <div ref={timeFieldRef} className={`auction-field${errorField === "time" ? " auction-field--error" : ""}`}>
              <label className="auction-field-label">
                Preferred {preference === "pickup" ? "Pick-Up" : "Delivery"} Date &amp; Time *
              </label>
              <input
                type="datetime-local"
                className={`auction-field-input${errorField === "time" ? " auction-field-input--error" : ""}`}
                min={new Date().toISOString().slice(0, 16)}
                value={preferredTime}
                onChange={(e) => { setPreferredTime(e.target.value); setError(""); setErrorField(null); }}
              />
            </div>
          )}

          <div className="auction-fulfillment-total">
            <div className="auction-price-breakdown">
              <div className="auction-price-row">
                <span>Winning bid</span>
                <span>{formatDollars(winningBid)}</span>
              </div>
              {tax > 0 && (
                <div className="auction-price-row auction-price-row--tax">
                  <span>Tax ({tax}%)</span>
                  <span>{formatDollars(bidTax)}</span>
                </div>
              )}
              <div className="auction-price-row auction-price-row--total">
                <span>Total charged</span>
                <span>{formatDollars(bidTotal)}</span>
              </div>
            </div>
            {preference === "delivery" && (
              <div className="auction-price-breakdown auction-price-breakdown--delivery">
                <div className="auction-price-row">
                  <span>Delivery fee</span>
                  <span>{formatDollars(deliveryFeeBase)}</span>
                </div>
                {tax > 0 && (
                  <div className="auction-price-row auction-price-row--tax">
                    <span>Tax ({tax}%)</span>
                    <span>{formatDollars(deliveryTax)}</span>
                  </div>
                )}
                <div className="auction-price-row auction-price-row--total">
                  <span>Delivery total — charged to {paymentLabel}</span>
                  <span>{formatDollars(deliveryTotal)}</span>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="auction-form-error" role="alert">
              <FaTimes /> {error}
            </div>
          )}

          {preference !== "pickup_now" && (
            <p className="auction-fulfillment-disclaimer">
              ⚠ If we miss your preferred time and cannot reach you at the number you provided, you are responsible to reach out for an alternative pickup or delivery.
            </p>
          )}

          <button type="submit" className="auction-submit-btn" disabled={submitting}>
            {submitting ? (
              <><span className="btn-spinner" /> {preference === "delivery" ? "Charging & Confirming…" : "Confirming…"}</>
            ) : preference === "pickup_now" ? (
              <><FaCheckCircle /> {isEditing ? "Update to Pick Up Tonight" : "Confirm — I'm Taking It Home"}</>
            ) : (
              <><FaCheckCircle /> {isEditing ? "Update" : "Confirm"} {preference === "pickup" ? "Pick-Up" : "Delivery"}</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Bid Modal ──────────────────────────────────────────────────────────────────

const MIN_BID = 1.00; // Minimum initial bid

function BidModal({ item, email, customerName, customerPhone, taxPercent, isLeading, onClose, onSuccess }) {
  const [name, setName] = useState(customerName || "");
  const [phone, setPhone] = useState(() => {
    if (customerPhone) {
      // customer_phone is E.164 (e.g. "+13145550100") — take last 10 digits and format
      return formatPhone(customerPhone.replace(/\D/g, "").slice(-10));
    }
    return "";
  });
  const [localFloor, setLocalFloor] = useState(parseFloat(item.current_bid));
  const [localBidCount, setLocalBidCount] = useState(item.bid_count);
  const [amountStr, setAmountStr] = useState(
    Math.max(parseFloat(item.current_bid) + SUGGEST_INCREMENT, MIN_BID).toFixed(2)
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [successAmount, setSuccessAmount] = useState(null);
  const nameRef = useRef(null);
  const modalRef = useRef(null);
  useKeyboardScrollReset(modalRef);

  const floor = localFloor;
  const amountVal = parseFloat(amountStr) || 0;
  const tax = taxPercent || 0;
  const estimatedTax = amountVal * (tax / 100);
  const estimatedTotal = amountVal + estimatedTax;

  useEffect(() => {
    if (nameRef.current) nameRef.current.focus();
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape" && !success) onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [success, onClose]);

  const adjust = (delta) => {
    const next = parseFloat((amountVal + delta).toFixed(2));
    if (next > floor) setAmountStr(next.toFixed(2));
  };

  const handleAmountBlur = () => {
    const val = parseFloat(amountStr);
    if (!isNaN(val) && val > 0) {
      setAmountStr(val.toFixed(2));
    } else {
      setAmountStr(Math.max(floor + SUGGEST_INCREMENT, MIN_BID).toFixed(2));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) { setError("Please enter your name."); return; }
    if (amountVal < MIN_BID) {
      setError(`Minimum bid is ${formatDollars(MIN_BID)}.`);
      return;
    }
    if (amountVal <= floor) {
      setError(`Your bid must be more than ${formatDollars(floor)}.`);
      return;
    }
    setSubmitting(true);
    try {
      const result = await placeBid(item.event_id, item.item_id, {
        customer_name: name.trim(),
        customer_email: email,
        customer_phone: phone.trim() || undefined,
        bid_amount: parseFloat(amountVal.toFixed(2)),
        customer_token: item._customerToken,
      });
      setLocalFloor(result.bid_amount);
      setLocalBidCount((c) => c + 1);
      setSuccessAmount(result.bid_amount);
      setSuccess(true);
      setTimeout(() => onSuccess(result.bid_amount), 2200);
    } catch (err) {
      const msg = err.message || "";
      if (msg.toLowerCase().includes("bid must be higher")) {
        const match = msg.match(/\$([\d,]+\.?\d*)/);
        const newFloor = match ? parseFloat(match[1].replace(",", "")) : floor;
        setLocalFloor(newFloor);
        setLocalBidCount((c) => c + 1);
        setAmountStr((newFloor + SUGGEST_INCREMENT).toFixed(2));
        setError(`Someone just bid ${formatDollars(newFloor)} — enter a higher amount to stay in.`);
      } else {
        setError(msg || "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auction-modal-overlay" role="dialog" aria-modal="true">
      <div className="auction-modal" ref={modalRef}>
        {!success ? (
          <>
            <button className="auction-modal-close" onClick={onClose} aria-label="Close">
              <FaTimes />
            </button>

            <div className="auction-modal-header">
              <div className="auction-modal-gavel-wrap">
                <FaGavel className="auction-modal-gavel" />
              </div>
              <h2 className="auction-modal-heading">Place Your Bid</h2>
              <p className="auction-modal-item-name">{item.title}</p>
              {item.artist_name && (
                <p className="auction-modal-artist">by {item.artist_name}</p>
              )}
            </div>

            {isLeading && (
              <div className="auction-modal-leading-notice">
                <FaCheckCircle /> You're currently the highest bidder
              </div>
            )}

            <div className="auction-modal-current-bid">
              <div className="auction-modal-current-inner">
                <span className="auction-modal-current-label">
                  {localBidCount > 0 ? "High Bid" : "Starting Bid"}
                </span>
                <span className="auction-modal-current-value">
                  {formatDollars(localFloor)}
                </span>
                {localBidCount > 0 && (
                  <span className="auction-modal-bid-count">
                    <FaUsers />
                    {localBidCount} bid{localBidCount !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>

            <form className="auction-bid-form" onSubmit={handleSubmit}>
              <div className="auction-field">
                <label className="auction-field-label">Your Name *</label>
                <input
                  ref={nameRef}
                  type="text"
                  className="auction-field-input"
                  placeholder="Full name"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setError(""); }}
                  required
                />
              </div>

              <div className="auction-field">
                <label className="auction-field-label">Email</label>
                <input
                  type="email"
                  className="auction-field-input auction-field-input--readonly"
                  value={email}
                  readOnly
                />
              </div>

              <div className="auction-field">
                <label className="auction-field-label">Phone <span className="auction-field-optional">(optional)</span></label>
                <PhoneInput
                  className="auction-field-input"
                  placeholder="(314) 555-0100"
                  value={phone}
                  onChange={(v) => setPhone(v)}
                />
              </div>

              <div className="auction-field auction-field--amount">
                <label className="auction-field-label">Your Bid *</label>
                <div className="auction-amount-row">
                  <button
                    type="button"
                    className="auction-stepper-btn"
                    onClick={() => adjust(-5)}
                    disabled={amountVal - 5 <= floor}
                    aria-label="Decrease bid by $5"
                  >
                    <FaChevronDown />
                  </button>
                  <div className="auction-amount-display">
                    <span className="auction-amount-dollar">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="auction-amount-input"
                      value={amountStr}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
                        setAmountStr(raw);
                        setError("");
                      }}
                      onBlur={handleAmountBlur}
                    />
                  </div>
                  <button
                    type="button"
                    className="auction-stepper-btn"
                    onClick={() => adjust(5)}
                    aria-label="Increase bid by $5"
                  >
                    <FaChevronUp />
                  </button>
                </div>
                <p className="auction-amount-hint">
                  Must beat current bid — minimum {formatDollars(Math.max(floor + 0.01, MIN_BID))}
                </p>
              </div>

              {error && (
                <div className="auction-form-error" role="alert">
                  <FaTimes /> {error}
                </div>
              )}

              {amountVal > floor && (
                <div className="auction-price-breakdown auction-price-breakdown--bid">
                  <div className="auction-price-row">
                    <span>Your bid</span>
                    <span>{formatDollars(amountVal)}</span>
                  </div>
                  {tax > 0 && (
                    <div className="auction-price-row auction-price-row--tax">
                      <span>Tax ({tax}%)</span>
                      <span>{formatDollars(estimatedTax)}</span>
                    </div>
                  )}
                  <div className="auction-price-row auction-price-row--total">
                    <span>Est. total if you win</span>
                    <span>{formatDollars(estimatedTotal)}</span>
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="auction-submit-btn"
                disabled={submitting || amountVal <= floor || amountVal < MIN_BID}
              >
                {submitting ? (
                  <><span className="btn-spinner" /> Placing Bid…</>
                ) : (
                  <><FaGavel /> Bid {formatDollars(amountVal)}</>
                )}
              </button>

              <p className="auction-bid-disclaimer">
                By bidding you agree your saved payment method will be automatically charged if you win.
              </p>
            </form>
          </>
        ) : (
          <div className="auction-modal-success">
            <div className="auction-success-trophy">
              <FaCheckCircle />
            </div>
            <h2 className="auction-success-heading">You're the Highest Bidder!</h2>
            <div className="auction-success-amount">{formatDollars(successAmount)}</div>
            <p className="auction-success-sub">
              Great bid! Bids refresh every few seconds — keep an eye on the auction to stay on top.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Buy Now Modal ──────────────────────────────────────────────────────────────

function BuyNowModal({ item, taxPercent, paymentStatus, onClose, onSuccess }) {
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState("");

  const tax = taxPercent || 0;
  const price = parseFloat(item.buy_now_price);
  const taxAmount = price * (tax / 100);
  const total = price + taxAmount;

  const paymentLabel = paymentStatus?.type === "paypal"
    ? `PayPal (${paymentStatus.paypal_email || "linked"})`
    : paymentStatus?.last4
      ? `${paymentStatus.brand || "Card"} ····${paymentStatus.last4}`
      : "your saved payment method";

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleConfirm = async () => {
    setPurchasing(true);
    setError("");
    try {
      const result = await buyNow(item.event_id, item.item_id, item._customerToken);
      onSuccess(result);
    } catch (err) {
      setError(err.message || "Purchase failed. Please try again.");
      setPurchasing(false);
    }
  };

  return (
    <div className="auction-modal-overlay" role="dialog" aria-modal="true">
      <div className="auction-modal">
        <button className="auction-modal-close" onClick={onClose} aria-label="Close">
          <FaTimes />
        </button>

        <div className="auction-modal-header">
          <div className="auction-modal-gavel-wrap">
            <FaTag className="auction-modal-gavel" />
          </div>
          <h2 className="auction-modal-heading">Buy Now</h2>
          <p className="auction-modal-item-name">{item.title}</p>
          {item.artist_name && (
            <p className="auction-modal-artist">by {item.artist_name}</p>
          )}
        </div>

        <div className="auction-modal-current-bid">
          <div className="auction-modal-current-inner">
            <span className="auction-modal-current-label">Buy Now Price</span>
            <span className="auction-modal-current-value">{formatDollars(price)}</span>
          </div>
        </div>

        <div className="auction-bid-form">
          <p className="auction-fulfillment-intro">
            Pay this fixed price now and the item is yours immediately — no waiting for the auction to end.
          </p>

          <div className="auction-price-breakdown auction-price-breakdown--bid">
            <div className="auction-price-row">
              <span>Buy Now price</span>
              <span>{formatDollars(price)}</span>
            </div>
            {tax > 0 && (
              <div className="auction-price-row auction-price-row--tax">
                <span>Tax ({tax}%)</span>
                <span>{formatDollars(taxAmount)}</span>
              </div>
            )}
            <div className="auction-price-row auction-price-row--total">
              <span>Total charged to {paymentLabel}</span>
              <span>{formatDollars(total)}</span>
            </div>
          </div>

          {error && (
            <div className="auction-form-error" role="alert">
              <FaTimes /> {error}
            </div>
          )}

          <button
            type="button"
            className="auction-submit-btn"
            onClick={handleConfirm}
            disabled={purchasing}
          >
            {purchasing ? (
              <><span className="btn-spinner" /> Purchasing…</>
            ) : (
              <><FaTag /> Confirm Purchase — {formatDollars(total)}</>
            )}
          </button>

          <p className="auction-bid-disclaimer">
            By confirming, your saved payment method will be charged immediately and you win this item.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Image Lightbox ─────────────────────────────────────────────────────────────

function ImageLightbox({ src, alt, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="auction-lightbox-overlay" onClick={onClose}>
      <button className="auction-lightbox-close" onClick={onClose} aria-label="Close">
        <FaTimes />
      </button>
      <img
        src={src}
        alt={alt}
        className="auction-lightbox-img"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// ── Item Card ──────────────────────────────────────────────────────────────────

function AuctionItemCard({ item, onBid, onBuyNow, onClaim, justBid, isLeading, onImageClick, userEmail, onRetryPayment, retryingItemId }) {
  const isOpen = item.auction_status === "open";
  const isHot = item.bid_count >= 3;
  const isWinner =
    !isOpen &&
    item.winner_email &&
    userEmail &&
    item.winner_email.toLowerCase() === userEmail.toLowerCase();
  const alreadyClaimed = Boolean(item.delivery_preference);
  const paymentFailed = isWinner && (item.charge_status === "failed" || item.charge_status === "retry_failed");
  const isRetrying = retryingItemId === item.item_id;

  const deadlineLabel = (() => {
    if (!item.payment_retry_deadline) return null;
    const dl = new Date(item.payment_retry_deadline);
    if (isNaN(dl.getTime())) return null;
    return dl.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
  })();

  const deadlineExpired = (() => {
    if (!item.payment_retry_deadline) return false;
    const dl = new Date(item.payment_retry_deadline);
    return !isNaN(dl.getTime()) && dl < new Date();
  })();

  return (
    <article
      className={[
        "auction-card",
        !isOpen && "auction-card--closed",
        justBid && "auction-card--just-bid",
        isWinner && "auction-card--winner",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="auction-card-image-wrap">
        {item.image_url ? (
          <button
            className="auction-card-image-btn"
            onClick={() => onImageClick(item.image_url, item.title)}
            aria-label={`View full image of ${item.title}`}
          >
            <img src={item.image_url} alt={item.title} className="auction-card-image" loading="lazy" />
            <div className="auction-card-image-expand"><FaExpand /></div>
          </button>
        ) : (
          <div className="auction-card-image-placeholder"><FaTrophy /></div>
        )}

        <div className={`auction-card-status ${isOpen ? "auction-card-status--open" : "auction-card-status--closed"}`}>
          {isOpen ? <><span className="auction-live-dot" />Live</> : "Ended"}
        </div>

        {isOpen && isHot && (
          <div className="auction-card-hot"><FaFire /> Hot</div>
        )}

        {isWinner && (
          <div className="auction-card-winner-badge"><FaCrown /> You Won!</div>
        )}

        {isLeading && !justBid && (
          <div className="auction-card-leading"><FaCheckCircle /> You are the highest bidder!</div>
        )}

        {justBid && !isWinner && (
          <div className="auction-card-your-bid"><FaCheckCircle /> Your bid!</div>
        )}
      </div>

      <div className="auction-card-body">
        {item.artist_name && <p className="auction-card-artist">{item.artist_name}</p>}
        <h3 className="auction-card-title">{item.title}</h3>
        {item.description && <p className="auction-card-desc">{item.description}</p>}

        <div className="auction-card-footer">
          <div className="auction-card-bid-block">
            <span className="auction-card-bid-label">
              {isOpen ? "Current Bid" : "Final Bid"}
            </span>
            <span className={`auction-card-bid-amount ${justBid ? "auction-card-bid-amount--highlight" : ""} ${isWinner ? "auction-card-bid-amount--winner" : ""}`}>
              {formatDollars(item.current_bid)}
            </span>
            <span className="auction-card-bid-tally">
              <FaUsers />
              {item.bid_count === 0 ? "No bids yet — be first!" : `${item.bid_count} bid${item.bid_count !== 1 ? "s" : ""}`}
            </span>
            {isOpen && item.buy_now_price && (
              <span className="auction-card-buy-now-label">
                <FaTag /> Buy Now: {formatDollars(item.buy_now_price)}
              </span>
            )}
          </div>

          {isOpen ? (
            item.buy_now_price ? (
              <div className="auction-card-actions">
                <button className="auction-card-cta" onClick={() => onBid(item)}>
                  <FaGavel /><span>Place Bid</span>
                </button>
                <button className="auction-card-cta auction-card-cta--buy-now" onClick={() => onBuyNow(item)}>
                  <FaTag /><span>Buy Now</span>
                </button>
              </div>
            ) : (
              <button className="auction-card-cta" onClick={() => onBid(item)}>
                <FaGavel /><span>Place Bid</span>
              </button>
            )
          ) : isWinner ? (
            paymentFailed ? (
              <div className="auction-card-payment-failed">
                <div className="auction-payment-failed-badge">
                  <FaTimes />
                  {item.charge_status === "retry_failed" ? " Retry Failed" : " Payment Failed"}
                </div>
                {deadlineExpired ? (
                  <p className="auction-payment-failed-deadline auction-payment-failed-deadline--expired">
                    Retry window expired — contact support
                  </p>
                ) : deadlineLabel ? (
                  <p className="auction-payment-failed-deadline">Retry by {deadlineLabel}</p>
                ) : null}
                {!deadlineExpired && (
                  <button
                    className="auction-card-cta auction-card-cta--retry"
                    onClick={() => onRetryPayment(item)}
                    disabled={isRetrying}
                  >
                    {isRetrying
                      ? <><span className="btn-spinner" /> Retrying…</>
                      : <><FaCreditCard /> {item.charge_status === "retry_failed" ? "Try Again" : "Retry Payment"}</>
                    }
                  </button>
                )}
              </div>
            ) : alreadyClaimed ? (
              <button className="auction-card-claimed-pill auction-card-claimed-pill--edit" onClick={() => onClaim(item)} title="Change pickup / delivery">
                {item.delivery_preference === "delivery" ? <FaTruck /> : <FaStore />}
                {item.delivery_preference === "delivery" ? "Delivery" : "Pick Up"}
                <FaEdit className="auction-claimed-edit-icon" />
              </button>
            ) : (
              <button className="auction-card-cta auction-card-cta--winner" onClick={() => onClaim(item)}>
                <FaCrown /><span>Claim Prize</span>
              </button>
            )
          ) : (
            <div className="auction-card-ended-pill">Bidding Ended</div>
          )}
        </div>
      </div>
    </article>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function SilentAuctionPage() {
  const { eventSlug } = useParams();
  const navigate = useNavigate();
  const { email, customerToken, viewMode, handleLoginSuccess } = useAuth();

  const [event, setEvent] = useState(null);
  const [items, setItems] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState("");

  const [bidTarget, setBidTarget] = useState(null);
  const [buyNowTarget, setBuyNowTarget] = useState(null);
  const [fulfillTarget, setFulfillTarget] = useState(null);
  const [pendingItem, setPendingItem] = useState(null);
  const [pendingBidItemId, setPendingBidItemId] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [justBidSet, setJustBidSet] = useState(new Set());
  const [leadingBids, setLeadingBids] = useState({});
  const [userBidItems, setUserBidItems] = useState(new Set());
  const [shakePayment, setShakePayment] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [countdown, setCountdown] = useState(POLL_INTERVAL_CLOSED / 1000);
  const pollingRef = useRef(null);
  const pollIntervalRef = useRef(POLL_INTERVAL_CLOSED);

  const [paymentStatus, setPaymentStatus] = useState(null);
  const [linkingCard, setLinkingCard] = useState(false);
  const [linkingPaypal, setLinkingPaypal] = useState(false);
  const [returnHandled, setReturnHandled] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return (p.get("payment_setup") === "complete" && !!p.get("session_id")) ||
           (p.get("paypal_vault") === "return" && !!p.get("approval_token_id"));
  });
  const [retryingItemId, setRetryingItemId] = useState(null);

  // Load persisted bid items for this user+event from localStorage
  useEffect(() => {
    if (!email || !eventSlug) return;
    try {
      const saved = localStorage.getItem(`auction_user_bids_${eventSlug}_${email}`);
      if (saved) setUserBidItems(new Set(JSON.parse(saved)));
    } catch {}
  }, [email, eventSlug]);

  // Fetch event
  useEffect(() => {
    if (!eventSlug) return;
    let live = true;
    fetch(`${apiUrl}/loyalty/events/${eventSlug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => { if (live) setEvent(data); })
      .catch(() => { if (live) setError("Event not found."); });
    return () => { live = false; };
  }, [eventSlug]);

  // Fetch payment status when we have both token and event
  useEffect(() => {
    if (!customerToken || !event?.event_id) return;
    getPaymentStatus(event.event_id, customerToken)
      .then(setPaymentStatus)
      .catch(() => {});
  }, [customerToken, event?.event_id]);

  // Handle return from Stripe or PayPal setup
  useEffect(() => {
    if (!event?.event_id || !customerToken || returnHandled) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const approvalTokenId = params.get("approval_token_id");
    const isStripeReturn = params.get("payment_setup") === "complete" && sessionId;
    const isPaypalReturn = params.get("paypal_vault") === "return" && approvalTokenId;

    if (!isStripeReturn && !isPaypalReturn) return;
    setReturnHandled(true);

    const cleanUrl = () => {
      const url = new URL(window.location.href);
      ["payment_setup", "session_id", "paypal_vault", "approval_token_id"].forEach(k => url.searchParams.delete(k));
      window.history.replaceState({}, "", url.toString());
    };

    const resumePendingBid = () => {
      const savedId = localStorage.getItem(PENDING_BID_KEY);
      if (savedId) {
        localStorage.removeItem(PENDING_BID_KEY);
        setPendingBidItemId(parseInt(savedId, 10));
      }
    };

    const savedPref = localStorage.getItem(SAVE_PREF_KEY);
    const saveForNextTime = savedPref !== "0";
    localStorage.removeItem(SAVE_PREF_KEY);

    if (isStripeReturn) {
      confirmSetup(event.event_id, customerToken, sessionId, saveForNextTime)
        .then((result) => {
          setPaymentStatus({ has_payment_method: true, type: "stripe", last4: result.last4, brand: result.brand });
          cleanUrl();
          resumePendingBid();
        })
        .catch(() => cleanUrl())
        .finally(() => setConfirmingPayment(false));
    } else {
      confirmPaypal(event.event_id, customerToken, approvalTokenId, saveForNextTime)
        .then((result) => {
          setPaymentStatus({ has_payment_method: true, type: "paypal", paypal_email: result.paypal_email });
          cleanUrl();
          resumePendingBid();
        })
        .catch(() => cleanUrl())
        .finally(() => setConfirmingPayment(false));
    }
  }, [event?.event_id, customerToken, returnHandled]);

  // Auto-open bid modal once payment confirmed and item is still open
  useEffect(() => {
    if (!pendingBidItemId || !items.length || !customerToken || !paymentStatus?.has_payment_method) return;
    const item = items.find((i) => i.item_id === pendingBidItemId && i.auction_status === "open");
    if (item) {
      setPendingBidItemId(null);
      setBidTarget({ ...item, _customerToken: customerToken, _customerName: paymentStatus.customer_name });
    }
  }, [pendingBidItemId, items, customerToken, paymentStatus]);

  const loadItems = useCallback(async (eventId) => {
    try {
      const data = await fetchAuctionItems(eventId, email);
      setItems(data);
      setLastRefresh(Date.now());
      const hasOpen = data.some((i) => i.auction_status === "open");
      pollIntervalRef.current = hasOpen ? POLL_INTERVAL_OPEN : POLL_INTERVAL_CLOSED;
    } catch (err) {
      console.error("Auction poll failed:", err);
    } finally {
      setPageLoading(false);
    }
  }, [email]);

  useEffect(() => {
    if (!event?.event_id) return;
    loadItems(event.event_id);

    const scheduleTick = () => {
      pollingRef.current = setTimeout(async () => {
        await loadItems(event.event_id);
        scheduleTick();
      }, pollIntervalRef.current);
    };
    scheduleTick();

    return () => clearTimeout(pollingRef.current);
  }, [event?.event_id, loadItems]);

  useEffect(() => {
    const t = setInterval(() => {
      const elapsed = (Date.now() - lastRefresh) / 1000;
      const total = pollIntervalRef.current / 1000;
      setCountdown(Math.max(0, Math.round(total - elapsed)));
    }, 1000);
    return () => clearInterval(t);
  }, [lastRefresh]);

  // ── Payment setup helpers ──────────────────────────────────────────────────

  const doLinkCard = useCallback(async (tokenOverride) => {
    const tok = tokenOverride || customerToken;
    if (!event?.event_id || !tok) return;
    setLinkingCard(true);
    try {
      const base = window.location.href.split("?")[0];
      const { checkout_url } = await setupPayment(event.event_id, tok, base, base);
      window.location.href = checkout_url;
    } catch {
      setLinkingCard(false);
    }
  }, [event?.event_id, customerToken]);

  const doLinkPaypal = useCallback(async (tokenOverride) => {
    const tok = tokenOverride || customerToken;
    if (!event?.event_id || !tok) return;
    setLinkingPaypal(true);
    try {
      const base = window.location.href.split("?")[0];
      const { approve_url } = await setupPaypal(event.event_id, tok, base, base);
      window.location.href = approve_url;
    } catch (err) {
      console.error("[doLinkPaypal] error:", err?.message, err);
      setLinkingPaypal(false);
    }
  }, [event?.event_id, customerToken]);

  // const venmoSetupToken = useCallback(async () => {
  //   const { token_id } = await setupVenmoVault(event.event_id, customerToken);
  //   return token_id;
  // }, [event?.event_id, customerToken]);

  // const handleVenmoApprove = useCallback(async (vaultSetupToken) => {
  //   try {
  //     const result = await confirmVenmo(event.event_id, customerToken, vaultSetupToken, true);
  //     setPaymentStatus({ has_payment_method: true, type: "venmo", venmo_handle: result.venmo_handle });
  //     const savedId = localStorage.getItem(PENDING_BID_KEY);
  //     if (savedId) {
  //       localStorage.removeItem(PENDING_BID_KEY);
  //       setPendingBidItemId(parseInt(savedId, 10));
  //     }
  //   } catch (err) {
  //     console.error("Venmo confirm failed:", err);
  //   }
  // }, [event?.event_id, customerToken]);

  // ── Bid flow ───────────────────────────────────────────────────────────────

  const handleBidClick = (item) => {
    // Only show login modal for truly unauthenticated users
    if (!isLoggedIn()) {
      setPendingItem(item);
      setShowLoginModal(true);
      return;
    }
    // Payment status still loading — don't block
    if (paymentStatus === null) return;
    // No payment method — save item and let them pick card/PayPal from banner
    if (!paymentStatus.has_payment_method) {
      localStorage.setItem(PENDING_BID_KEY, String(item.item_id));
      document.querySelector(".auction-payment-banner")?.scrollIntoView({ behavior: "smooth", block: "center" });
      setShakePayment(true);
      setTimeout(() => setShakePayment(false), 600);
      return;
    }
    setBidTarget({ ...item, _customerToken: customerToken, _customerName: paymentStatus.customer_name });
  };

  const handleBuyNowClick = (item) => {
    if (!isLoggedIn()) {
      setPendingItem(item);
      setShowLoginModal(true);
      return;
    }
    if (paymentStatus === null) return;
    if (!paymentStatus.has_payment_method) {
      document.querySelector(".auction-payment-banner")?.scrollIntoView({ behavior: "smooth", block: "center" });
      setShakePayment(true);
      setTimeout(() => setShakePayment(false), 600);
      return;
    }
    setBuyNowTarget({ ...item, _customerToken: customerToken });
  };

  const handleBuyNowSuccess = (updatedItem) => {
    setBuyNowTarget(null);
    // Optimistically update the item in local state; next poll confirms
    setItems((prev) =>
      prev.map((it) =>
        it.item_id === updatedItem.item_id ? { ...it, ...updatedItem } : it
      )
    );
  };

  const handleLoginSuccessLocal = useCallback((loggedEmail, token) => {
    handleLoginSuccess(loggedEmail, token);
    setShowLoginModal(false);

    if (!pendingItem || !event?.event_id) return;
    const item = pendingItem;
    setPendingItem(null);

    // Use the token directly since AuthContext state hasn't updated yet
    getPaymentStatus(event.event_id, token)
      .then((status) => {
        setPaymentStatus(status);
        if (!status.has_payment_method) {
          localStorage.setItem(PENDING_BID_KEY, String(item.item_id));
        } else {
          setBidTarget({ ...item, _customerToken: token, _customerName: status.customer_name });
        }
      })
      .catch(() => {
        // Don't block bidding on payment status check failure
        setBidTarget({ ...item, _customerToken: token });
      });
  }, [handleLoginSuccess, pendingItem, event?.event_id]);

  const handleBidSuccess = (newAmount) => {
    const bidItemId = bidTarget.item_id;
    setItems((prev) =>
      prev.map((it) =>
        it.item_id === bidItemId
          ? { ...it, current_bid: newAmount, bid_count: it.bid_count + 1, last_bid_at: new Date().toISOString() }
          : it
      )
    );
    setJustBidSet((prev) => new Set([...prev, bidItemId]));
    setLeadingBids((prev) => ({ ...prev, [bidItemId]: newAmount }));
    setUserBidItems((prev) => {
      const next = new Set([...prev, bidItemId]);
      try {
        if (email && eventSlug) {
          localStorage.setItem(`auction_user_bids_${eventSlug}_${email}`, JSON.stringify([...next]));
        }
      } catch {}
      return next;
    });
    setBidTarget(null);
    setTimeout(() => {
      setJustBidSet((prev) => {
        const next = new Set(prev);
        next.delete(bidItemId);
        return next;
      });
    }, 12_000);
  };

  // Clear leading status when poll shows someone has outbid us
  useEffect(() => {
    if (!Object.keys(leadingBids).length) return;
    setLeadingBids((prev) => {
      const next = { ...prev };
      let changed = false;
      items.forEach((item) => {
        if (next[item.item_id] !== undefined && parseFloat(item.current_bid) > next[item.item_id]) {
          delete next[item.item_id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const handleFulfillSuccess = (preference) => {
    const itemId = fulfillTarget.item_id;
    setItems((prev) =>
      prev.map((it) => (it.item_id === itemId ? { ...it, delivery_preference: preference } : it))
    );
    setFulfillTarget(null);
  };

  const handleRetryPayment = useCallback(async (item) => {
    if (!customerToken) return;
    setRetryingItemId(item.item_id);
    try {
      await retryWinnerPayment(item.event_id, item.item_id, customerToken);
      // Optimistically mark as charged; next poll will confirm
      setItems((prev) =>
        prev.map((it) =>
          it.item_id === item.item_id
            ? { ...it, charge_status: "charged", is_paid: true }
            : it
        )
      );
    } catch (err) {
      // Keep retry button visible by updating status to retry_failed
      setItems((prev) =>
        prev.map((it) =>
          it.item_id === item.item_id
            ? { ...it, charge_status: "retry_failed" }
            : it
        )
      );
      alert(err.message || "Payment failed. Please update your payment method and try again.");
    } finally {
      setRetryingItemId(null);
    }
  }, [customerToken]);

  if (pageLoading && items.length === 0) return <LoadingPage />;

  if (error) {
    return (
      <div className="auction-page">
        <div className="auction-empty-state">
          <FaTrophy className="auction-empty-icon" />
          <h2>Event Not Found</h2>
          <p>{error}</p>
          <button className="auction-back-btn" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }

  const openCount = items.filter((i) => i.auction_status === "open").length;
  const allEnded = items.length > 0 && openCount === 0;
  const wonItems = items.filter(
    (i) =>
      i.auction_status === "closed" &&
      i.winner_email &&
      email &&
      i.winner_email.toLowerCase() === email.toLowerCase()
  );

  return (
    <div className="auction-page">
      {/* ── Hero ── */}
      <div className="auction-hero">
        <div className="auction-hero-inner">
          <div className="auction-hero-eyebrow">
            <FaGavel className="auction-hero-gavel" />
            <span>Silent Auction</span>
          </div>

          <h1 className="auction-hero-title">{event?.title || "Auction"}</h1>

          {allEnded ? (
            wonItems.length > 0 ? (
              <p className="auction-hero-sub auction-hero-sub--winner">
                Congratulations! You won {wonItems.length} item{wonItems.length !== 1 ? "s" : ""} — claim your prize below.
              </p>
            ) : (
              <p className="auction-hero-sub auction-hero-sub--ended">
                Bidding has ended — thank you to everyone who participated!
              </p>
            )
          ) : openCount > 0 ? (
            <p className="auction-hero-sub">
              Support local artists. Bid on one-of-a-kind works.
              Bids are live — the highest bidder wins when the auction closes.
            </p>
          ) : null}
        </div>

        {items.length > 0 && (
          <div className="auction-refresh-bar">
            <FaClock />
            <span>{countdown > 0 ? `Refreshing in ${countdown}s` : "Refreshing…"}</span>
            <span className="auction-refresh-bar-dot" />
            <span>{openCount} item{openCount !== 1 ? "s" : ""} open</span>
          </div>
        )}
      </div>

      {/* ── Payment Banner — logged-in users only ── */}
      {isLoggedIn() && (paymentStatus !== null || confirmingPayment) && (
        <PaymentBanner
          paymentStatus={paymentStatus}
          onLinkCard={() => doLinkCard()}
          onLinkPaypal={() => doLinkPaypal()}
          linkingCard={linkingCard}
          linkingPaypal={linkingPaypal}
          confirmingPayment={confirmingPayment}
          shakePayment={shakePayment}
          // venmoSetupToken={venmoSetupToken}
          // onVenmoApprove={handleVenmoApprove}
          // onVenmoError={(msg) => console.error("Venmo error:", msg)}
        />
      )}

      {/* ── Items ── */}
      {items.length > 0 ? (
        <section className="auction-grid-section">
          <div className="auction-grid">
            {[...items]
              .sort((a, b) => {
                const aOpen = a.auction_status === 'open';
                const bOpen = b.auction_status === 'open';
                // Open items always before closed
                if (aOpen !== bOpen) return aOpen ? -1 : 1;
                if (aOpen) {
                  // #1 priority: items the current user has bid on
                  const aUserBid = userBidItems.has(a.item_id);
                  const bUserBid = userBidItems.has(b.item_id);
                  if (aUserBid !== bUserBid) return aUserBid ? -1 : 1;
                  // #2: most recently bid by anyone
                  const aTime = a.last_bid_at ? new Date(a.last_bid_at).getTime() : 0;
                  const bTime = b.last_bid_at ? new Date(b.last_bid_at).getTime() : 0;
                  if (bTime !== aTime) return bTime - aTime;
                  // #3: highest current bid
                  return parseFloat(b.current_bid) - parseFloat(a.current_bid);
                }
                return 0;
              })
              .map((item) => (
              <AuctionItemCard
                key={item.item_id}
                item={item}
                onBid={handleBidClick}
                onBuyNow={handleBuyNowClick}
                onClaim={setFulfillTarget}
                justBid={justBidSet.has(item.item_id)}
                isLeading={Boolean(item.is_leading) || leadingBids[item.item_id] !== undefined}
                onImageClick={(src, alt) => setLightbox({ src, alt })}
                userEmail={email}
                onRetryPayment={handleRetryPayment}
                retryingItemId={retryingItemId}
              />
            ))}
          </div>
        </section>
      ) : (
        !pageLoading && (
          <div className="auction-empty-state">
            <FaTrophy className="auction-empty-icon" />
            <h2>Items dropping soon</h2>

            {paymentStatus?.has_payment_method ? (
              <>
                <p>
                  You're all set. Your payment method is linked and ready —
                  we'll have items up before you know it.
                </p>
                <span className="auction-prelink-hint--ready">
                  <FaCheckCircle /> Ready to bid
                </span>
              </>
            ) : (
              <>
                <p>
                  {!isLoggedIn()
                    ? "Don't wait for the items to drop. Sign in and link your payment method now — you'll be the first one ready to bid the moment things go live."
                    : "While you wait, link a payment method so you're all set the moment bidding opens. It only takes a second and you'll be charged only if you win."}
                </p>
                <button
                  className="auction-prelink-btn"
                  onClick={() => {
                    if (!isLoggedIn()) {
                      setShowLoginModal(true);
                    } else {
                      document.querySelector(".auction-payment-banner")
                        ?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                  }}
                >
                  <FaCreditCard />
                  {!isLoggedIn() ? "Sign in & link payment" : "Link payment method"}
                </button>
              </>
            )}
          </div>
        )
      )}

      {/* ── Modals ── */}
      <EmailOTPModal
        isOpen={showLoginModal}
        onClose={() => { setShowLoginModal(false); setPendingItem(null); }}
        onLoginSuccess={handleLoginSuccessLocal}
      />

      {bidTarget && (
        <BidModal
          item={bidTarget}
          email={email}
          customerName={bidTarget._customerName}
          customerPhone={paymentStatus?.customer_phone}
          taxPercent={paymentStatus?.auction_tax_percent ?? 0}
          isLeading={Boolean(bidTarget.is_leading) || leadingBids[bidTarget.item_id] !== undefined}
          onClose={() => setBidTarget(null)}
          onSuccess={handleBidSuccess}
        />
      )}

      {buyNowTarget && (
        <BuyNowModal
          item={buyNowTarget}
          taxPercent={paymentStatus?.auction_tax_percent ?? 0}
          paymentStatus={paymentStatus}
          onClose={() => setBuyNowTarget(null)}
          onSuccess={handleBuyNowSuccess}
        />
      )}

      {fulfillTarget && (
        <FulfillmentModal
          item={fulfillTarget}
          customerToken={customerToken}
          paymentStatus={paymentStatus}
          taxPercent={paymentStatus?.auction_tax_percent ?? 0}
          venue={event?.primary_venue}
          onClose={() => setFulfillTarget(null)}
          onSuccess={handleFulfillSuccess}
        />
      )}

      {lightbox && (
        <ImageLightbox
          src={lightbox.src}
          alt={lightbox.alt}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
